import React, { useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";

import { postApiMediaCall } from "../../../service/httpCall";
import { API_MOH_CREATE } from "../../../service/url";
import { ROUTES } from "../../../utilities/constant";
import LottieAnimation from "../../../components/lottie/lottie";
import "../../../assets/custom/css/custom.css";

const NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

const AddMoh = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [mohName, setMohName] = useState("");
  const [mohType, setMohType] = useState("outgoing");
  const [nameError, setNameError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showToastModal, setShowToastModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const showToasts = (msg) => {
    setToastMessage(msg);
    setShowToastModal(true);
    setTimeout(() => setShowToastModal(false), 2500);
  };

  const validateName = (val) => {
    if (!val || val.trim() === "") {
      setNameError("MOH name is required");
      return false;
    }
    if (val.includes(" ")) {
      setNameError("MOH name cannot contain spaces");
      return false;
    }
    if (!NAME_REGEX.test(val)) {
      setNameError("Only letters, numbers, underscore (_) and hyphen (-) allowed");
      return false;
    }
    setNameError("");
    return true;
  };

  const handleNameChange = (e) => {
    const v = e.target.value;
    setMohName(v);
    if (v.trim() !== "") validateName(v);
    else setNameError("");
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (f) {
      const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
      if (![".mp3", ".wav"].includes(ext)) {
        toast.error("Only .mp3 or .wav files allowed");
        e.target.value = "";
        setFile(null);
        return;
      }
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a .mp3 or .wav file");
      return;
    }
    if (!validateName(mohName)) {
      toast.error("Please fix the MOH name errors before uploading");
      return;
    }
    if (!["incoming", "outgoing"].includes(mohType)) {
      toast.error("Invalid MOH type");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("moh_name", mohName.trim());
    formData.append("moh_type", mohType);

    try {
      setIsUploading(true);
      const resp = await postApiMediaCall(API_MOH_CREATE, formData);
      const msg =
        mohType === "incoming"
          ? "MOH file uploaded successfully (auto-approved)."
          : "MOH file uploaded successfully. Please wait for Admin Approval.";
      showToasts(msg);
      setTimeout(() => navigate(`/${ROUTES.VOICE_LIBRARY.MOH_MANAGEMENT}`), 1500);
    } catch (err) {
      toast.error(typeof err === "string" ? err : err?.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="card-title">Upload MOH File</h4>
                <Button
                  label="Back to MOH List"
                  className="p-button-secondary p-button-sm"
                  onClick={() => navigate(`/${ROUTES.VOICE_LIBRARY.MOH_MANAGEMENT}`)}
                />
              </Card.Header>
              <Card.Body>
                <form onSubmit={(e) => e.preventDefault()}>
                  <Row className="mb-3">
                    <Col md="6">
                      <label htmlFor="uploadFile" className="form-label">
                        Upload WAV or MP3 File
                        <span style={{ color: "red", margin: "0 4px" }}>*</span>
                      </label>
                      <input
                        type="file"
                        id="uploadFile"
                        className="form-control"
                        accept=".wav,.mp3,audio/wav,audio/mpeg"
                        onChange={handleFileChange}
                      />
                    </Col>
                    <Col md="6">
                      <label htmlFor="mohName" className="form-label">
                        MOH Name <span style={{ color: "red", margin: "0 4px" }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="mohName"
                        value={mohName}
                        onChange={handleNameChange}
                        onKeyDown={(e) => {
                          if (e.key === " ") e.preventDefault();
                        }}
                        onBlur={() => validateName(mohName)}
                        className="form-control"
                        placeholder={
                          nameError
                            ? "Invalid name"
                            : "MOH Name (letters, numbers, _, - only)"
                        }
                        style={{
                          borderColor: nameError ? "#dc3545" : undefined,
                          backgroundColor: nameError ? "#fff5f5" : undefined,
                        }}
                      />
                      {nameError && (
                        <div
                          className="text-danger"
                          style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}
                        >
                          {nameError}
                        </div>
                      )}
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md="6">
                      <label htmlFor="mohType" className="form-label">
                        MOH Type <span style={{ color: "red", margin: "0 4px" }}>*</span>
                      </label>
                      <Form.Select
                        id="mohType"
                        value={mohType}
                        onChange={(e) => setMohType(e.target.value)}
                        className="form-control p-column-filter my-dropdown"
                        style={{ height: "50%" }}
                      >
                        <option value="outgoing">Outgoing</option>
                        <option value="incoming">Incoming</option>
                      </Form.Select>
                      <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                        Incoming type skips the approval step.
                      </small>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12" className="d-flex justify-content-end">
                      <Button
                        label={isUploading ? "Uploading..." : "Upload MOH"}
                        className="p-button-success"
                        onClick={handleUpload}
                        disabled={isUploading}
                        type="button"
                      />
                    </Col>
                  </Row>
                </form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal show={showToastModal} centered onHide={() => setShowToastModal(false)} size="sm">
        <Modal.Body className="text-center">
          <h5>{toastMessage}</h5>
          <LottieAnimation />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddMoh;
